import { useState, useMemo } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit from '../useLeadSubmit';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function QuizSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {}, currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'stepped';
    const showProgress = config.show_progress !== false;

    const questions = content.questions || [];
    const results = content.results || [];

    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [completed, setCompleted] = useState(false);

    const totalSteps = questions.length;
    const progressPct = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

    const handleSelect = (qIndex, value) => {
        setAnswers(prev => ({ ...prev, [qIndex]: value }));
        if (variant === 'stepped' && qIndex < totalSteps - 1) {
            setTimeout(() => setCurrentStep(qIndex + 1), 350);
        }
    };

    const leadSubmitHook = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'quiz',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: content.title || 'Quiz',
    });

    const handleFinish = () => {
        setCompleted(true);
        // Build readable answers for lead data
        const quizData = {};
        questions.forEach((q, i) => {
            if (answers[i] !== undefined) {
                quizData[q.text || `Pregunta ${i + 1}`] = answers[i];
            }
        });
        leadSubmitHook.submit(quizData);
    };

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleRestart = () => {
        setAnswers({});
        setCurrentStep(0);
        setCompleted(false);
    };

    const matchedResult = useMemo(() => {
        if (!completed || results.length === 0) return null;
        const selectedValues = Object.values(answers);
        let bestResult = results[0];
        let bestScore = 0;
        results.forEach(result => {
            const matchValues = result.match_values || [];
            const score = matchValues.filter(v => selectedValues.includes(v)).length;
            if (score > bestScore) {
                bestScore = score;
                bestResult = result;
            }
        });
        return bestResult;
    }, [completed, answers, results]);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cardBg = s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff';
    const cardBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const optionBg = s.dark ? 'rgba(255,255,255,0.04)' : '#f9fafb';
    const optionBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

    if (questions.length === 0) return null;

    const ProgressBar = () => {
        if (!showProgress || completed) return null;
        return (
            <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium" style={{ color: s.textSub }}>
                        Pregunta {currentStep + 1} de {totalSteps}
                    </span>
                    <span className="text-xs font-bold" style={{ color: s.accent }}>
                        {Math.round(progressPct)}%
                    </span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                    <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPct}%`, backgroundColor: s.accent }} />
                </div>
            </div>
        );
    };

    const QuestionCard = ({ question, qIndex, animDelay = 0 }) => {
        const selected = answers[qIndex];
        const options = question.options || [];
        return (
            <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: visible ? `${animDelay}ms` : '0ms' }}>
                <h3 className="text-lg md:text-xl font-bold mb-6" style={{ color: s.text }}>{question.text}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {options.map((opt, oi) => {
                        const isSelected = selected === opt.value;
                        return (
                            <button
                                key={oi}
                                onClick={() => handleSelect(qIndex, opt.value)}
                                className={`relative p-4 rounded-xl border-2 text-left transition-all duration-300 hover:shadow-md ${isSelected ? 'shadow-md scale-[1.02]' : 'hover:scale-[1.01]'}`}
                                style={{
                                    backgroundColor: isSelected ? hexToRgba(s.accent, s.dark ? 0.15 : 0.06) : optionBg,
                                    borderColor: isSelected ? s.accent : optionBorder,
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    {opt.icon && <span className="text-2xl">{opt.icon}</span>}
                                    <span className="text-sm font-semibold" style={{ color: isSelected ? s.accent : s.text }}>{opt.text}</span>
                                </div>
                                {isSelected && (
                                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: s.accent }}>
                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const ResultCard = () => {
        if (!matchedResult) return null;
        const folderImages = matchedResult.image_folder_id && mediaFolders ? (mediaFolders[matchedResult.image_folder_id] || []) : [];
        const image = folderImages[0]?.image_url || matchedResult.image_url || null;
        return (
            <div className={`max-w-xl mx-auto rounded-2xl border overflow-hidden transition-all duration-700 ${completed ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                {image && (
                    <div className="aspect-video overflow-hidden">
                        <img src={image} alt={matchedResult.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                )}
                <div className="p-6 md:p-8 text-center">
                    <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                        <svg className="w-6 h-6" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold mb-3" style={{ color: s.text }}>{matchedResult.title}</h3>
                    {matchedResult.description && (
                        <p className="text-sm leading-relaxed mb-6" style={{ color: s.textSub }}>{matchedResult.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 justify-center">
                        {matchedResult.cta_text && (
                            <a {...resolveLinkProps(matchedResult.cta_link || '#')}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:shadow-lg"
                                style={{ backgroundColor: s.accent }}>
                                {matchedResult.cta_text}
                            </a>
                        )}
                        <button
                            onClick={handleRestart}
                            className="px-6 py-3 rounded-xl font-semibold text-sm border-2 transition-all hover:opacity-80"
                            style={{ borderColor: s.accent, color: s.accent }}
                        >
                            Reiniciar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // All questions answered check
    const allAnswered = Object.keys(answers).length >= totalSteps;

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {completed ? (
                    <ResultCard />
                ) : variant === 'stepped' ? (
                    <div className="max-w-2xl mx-auto">
                        <ProgressBar />
                        <div className="rounded-2xl border p-6 md:p-8" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                            <QuestionCard question={questions[currentStep]} qIndex={currentStep} />
                            <div className="flex items-center justify-between mt-8">
                                <button
                                    onClick={handleBack}
                                    disabled={currentStep === 0}
                                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${currentStep === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:opacity-80'}`}
                                    style={{ color: s.textSub }}
                                >
                                    <span className="flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                        Anterior
                                    </span>
                                </button>
                                {currentStep === totalSteps - 1 && answers[currentStep] !== undefined ? (
                                    <button
                                        onClick={handleFinish}
                                        className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105 hover:shadow-lg"
                                        style={{ backgroundColor: s.accent }}
                                    >
                                        Ver resultado
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => answers[currentStep] !== undefined && setCurrentStep(currentStep + 1)}
                                        disabled={answers[currentStep] === undefined}
                                        className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${answers[currentStep] === undefined ? 'opacity-30 cursor-not-allowed' : 'hover:opacity-80'}`}
                                        style={{ color: s.accent }}
                                    >
                                        <span className="flex items-center gap-1">
                                            Siguiente
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* all-at-once */
                    <div className="max-w-2xl mx-auto space-y-8">
                        {questions.map((q, i) => (
                            <div key={i} className="rounded-2xl border p-6 md:p-8" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ backgroundColor: s.accent }}>
                                        {i + 1}
                                    </span>
                                    <span className="text-xs font-medium" style={{ color: s.textSub }}>Pregunta {i + 1} de {totalSteps}</span>
                                </div>
                                <QuestionCard question={q} qIndex={i} animDelay={i * 100} />
                            </div>
                        ))}
                        {allAnswered && (
                            <div className="text-center">
                                <button
                                    onClick={handleFinish}
                                    className="px-8 py-3.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:shadow-lg"
                                    style={{ backgroundColor: s.accent }}
                                >
                                    Ver resultado
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
